/**
 * Composant de rendu visuel d'une grille tarifaire SESAM
 * Reproduit fidèlement la maquette : bannière bleue par type + illustration + table 4 colonnes.
 * Réutilisé par : Tarifs wizard (aperçu étape 3), DocumentsSection (grille officielle).
 */
import { useMemo } from 'react';

const fmtInt = (n) => {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString('fr-FR').replace(/,/g, ' ');
};

// Métadonnées visuelles par type de panneau (titre, sous-titre tarif de référence, couleur)
const SECTION_META = {
  'Panneau déroulant': {
    title: (n) => `Panneaux déroulants (${n} emplacement${n > 1 ? 's' : ''})`,
    subtitle: 'Tarif : 3 000 FCFA / jour / affiche | 10 affiches par panneau',
    visual: 'deroulant',
  },
  'Panneau LED': {
    title: (n) => `Panneaux LED intérieur (${n} emplacement${n > 1 ? 's' : ''})`,
    subtitle: 'Tarif : 2 000 FCFA / jour / affiche | 10 affiches par panneau',
    visual: 'led',
  },
  'Grand format': {
    title: (n) => `Panneau grand format (${n} emplacement${n > 1 ? 's' : ''})`,
    subtitle: 'Tarif : 10 000 FCFA / jour / panneau (équivalent 10 affiches)',
    visual: 'grand',
  },
};

const TYPE_ORDER = { 'Panneau déroulant': 0, 'Panneau LED': 1, 'Grand format': 2 };

function VisualPanel({ type }) {
  if (type === 'deroulant') {
    return (
      <div className="flex gap-2 items-end justify-center">
        {[0, 1].map((i) => (
          <div key={i} className="w-16 rounded-sm overflow-hidden border-2 border-black bg-black shadow-md">
            <div className={`h-28 ${i === 0 ? 'bg-gradient-to-b from-red-600 to-red-400' : 'bg-gradient-to-b from-blue-700 to-blue-400'}`} />
            <div className="h-2 bg-neutral-800" />
          </div>
        ))}
      </div>
    );
  }
  if (type === 'led') {
    return (
      <div className="flex gap-2 items-end justify-center">
        {[0, 1].map((i) => (
          <div key={i} className="w-16 rounded-sm overflow-hidden border-2 border-slate-900 bg-slate-900 shadow-md">
            <div
              className="h-28"
              style={{
                background: i === 0
                  ? 'linear-gradient(135deg,#F59E0B 0%,#7C3AED 55%,#1E3A8A 100%)'
                  : 'linear-gradient(135deg,#7C3AED 0%,#F59E0B 55%,#1E3A8A 100%)',
              }}
            />
            <div className="h-2 bg-neutral-800" />
          </div>
        ))}
      </div>
    );
  }
  // grand format
  return (
    <div className="flex flex-col items-center pt-2">
      <div className="bg-black p-1 rounded-sm shadow-md">
        <div className="bg-white h-16 w-32 flex items-center justify-center border border-neutral-300">
          <span className="text-xl font-black text-[#1B3170] tracking-widest">SESAM</span>
        </div>
      </div>
      <div className="w-1.5 h-6 bg-neutral-800" />
      <div className="w-14 h-1 bg-black" />
    </div>
  );
}

export default function TarifGridDisplay({ nom, sections, showTitle = true, className = '' }) {
  const ordered = useMemo(() => {
    return [...(sections || [])].sort(
      (a, b) => (TYPE_ORDER[a.type_panneau] ?? 99) - (TYPE_ORDER[b.type_panneau] ?? 99)
    );
  }, [sections]);

  return (
    <div className={`bg-white rounded-lg ${className}`} data-testid="tarif-grid-display">
      {showTitle && (
        <div className="text-center pt-4 pb-2">
          <div className="inline-flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-[#1B3170] flex items-center justify-center shadow-sm">
              <span className="text-[#1B3170] font-black text-sm">SESAM</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-center gap-3">
            <div className="h-[2px] w-14 bg-[#1B3170]" />
            <div className="text-2xl font-black text-slate-900 tracking-[0.15em]">TARIFS</div>
            <div className="h-[2px] w-14 bg-[#1B3170]" />
          </div>
          {nom && nom !== 'Aperçu' && (
            <div className="mt-2 text-sm text-slate-600 italic">{nom}</div>
          )}
        </div>
      )}

      <div className="px-4 pb-3 space-y-4">
        {ordered.map((s) => {
          const meta = SECTION_META[s.type_panneau] || {
            title: () => s.type_panneau,
            subtitle: '',
            visual: 'grand',
          };
          const items = s.items || [];
          return (
            <div key={s.type_panneau} className="space-y-2">
              <div className="bg-[#1B3170] text-white font-bold text-sm px-4 py-2 rounded">
                {meta.title(items.length)}
              </div>
              {meta.subtitle && (
                <div className="text-xs italic text-slate-600 pl-1">{meta.subtitle}</div>
              )}
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="w-40 align-middle text-center p-1">
                      <VisualPanel type={meta.visual} />
                    </td>
                    <td className="align-top">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="border border-slate-300 bg-slate-50 text-[#1B3170] font-bold text-center py-2 px-2">Type</th>
                            <th className="border border-slate-300 bg-slate-50 text-[#1B3170] font-bold text-center py-2 px-2">Tarif / jour</th>
                            <th className="border border-slate-300 bg-slate-50 text-[#1B3170] font-bold text-center py-2 px-2">Tarif / mois (30j)</th>
                            <th className="border border-slate-300 bg-slate-50 text-[#1B3170] font-bold text-center py-2 px-2">Année</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="border border-slate-300 text-center text-slate-400 py-3">
                                Aucun tarif
                              </td>
                            </tr>
                          ) : (
                            items.map((it, i) => (
                              <tr key={i}>
                                <td className="border border-slate-300 py-2 px-2 font-semibold text-slate-800">{it.label || '—'}</td>
                                <td className="border border-slate-300 py-2 px-2 text-center font-bold">{fmtInt(it.prix_jour)}</td>
                                <td className="border border-slate-300 py-2 px-2 text-center font-bold">{fmtInt(it.prix_mois)}</td>
                                <td className="border border-slate-300 py-2 px-2 text-center font-bold">{fmtInt(it.prix_annee)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 mt-2 py-3 px-4 grid grid-cols-3 gap-2 text-xs text-slate-800 font-semibold">
        <div className="flex items-center gap-1.5 justify-center">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          +236 75503 702
        </div>
        <div className="flex items-center gap-1.5 justify-center">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          Bangui
        </div>
        <div className="flex items-center gap-1.5 justify-center">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          www.sesamrca.com
        </div>
      </div>
    </div>
  );
}
